import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import { differenceInHours, differenceInMonths, isBefore, isPast, parseISO } from 'date-fns'
import { get, isEmpty } from 'lodash-es'
import { bindActionCreators } from 'redux'
import styledWeb from 'styled-components'
import styled from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  CALENDAR_HEADER_HELP_LINK_DA,
  CALENDAR_HEADER_HELP_LINK_OUS,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/constants'
import { isBookableEvent } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { COMMON_MIXPANEL_PROPERTIES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { useVisibleInterval } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useVisibleInterval'
import {
  toLocalISOString,
  toUTCISOString,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import {
  getProgramNameFromId,
  isDAEngageProgram,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  getDbtTeensSchedulingParticipantNames,
  getDbtTeensSchedulingSessionTypeOptions,
  getNextDbtTeensSessionNumber,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/dbtTeensScheduling'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  BookingMode,
  CalendarAppointmentsView,
  ConflictStrategy,
  EventExtendedProps,
  EventModality,
  isSameModality,
  LyraEventExtendedPropsNoTZ,
  ProviderCalendarEvent,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { Episode, EpisodeParticipantRole } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { APPOINTMENT_STATUS, MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'
import { DAInvoiceFailedModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/daInvoiceFailedModal/DAInvoiceFailedModal'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import {
  ProviderCalendar,
  ProviderCalendarRef,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/providerCalendar/ProviderCalendar'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import './providerCalendar.scss'
import { CalendarIsLiveConfirmationModal } from './CalendarIsLiveModal'
import { CalendarLandingPage } from './CalendarLandingPage'
import CalendarSetupCard from './CalendarSetupCard'
import {
  useGetAvailableCalendarsQuery,
  useGetCalendarProviderQuery,
  useLazyGetAppointmentBookedDetailsQuery,
  useLazyGetCalendarEventsQuery,
  useLazyGetGoogleEventQuery,
  usePatchCalendarEventMutation,
  usePostCalendarEventsMutation,
} from './data/calendarApi'
import { getCalendarShowCalendarLiveModal } from './data/calendarSelectors'
import { hideCalendarLiveModal } from './data/calendarSlice'
import { useCapacityUpdateFlow } from '../hooks/useCapacityUpdateFlow'
import { useCalendarConfiguration } from './data/hooks/useCalendarConfiguration'
import { useCalendarRequiresAuth } from './data/hooks/useCalendarRequiresAuth'
import useCalendarAlerts from './hooks/useCalendarAlerts'
import { useProviderCalendarLive } from './hooks/useProviderCalendarLive'
import { useTimeZone } from './hooks/useTimeZone'
import {
  createSyntheticEventFromCancelledAppointment,
  eventOverlapsWithLateCancellations,
  filterLateCancelledAppointments,
  getHPISelfPayBannerVariantForClient,
  getSessionExtendedProperties,
  mapEventToBookableEvent,
  mapExtendedProps,
} from './utils'
import { actions as mixpanelActions, mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import CapacitiesCalendar from '../common/components/availabilityCard/CapacitiesCalendar'
import {
  episodeStates,
  getMeetingFormats,
  MILITARY_ONE_SOURCE_EMPLOYER,
  NP_TESTER_ROLES,
  programConfig,
} from '../common/constants/appConstants'
import {
  CLIENT_HOME,
  CLIENTS_CANCEL_SESSION,
  CLIENTS_RESCHEDULE_SESSION_DETAILS,
  LC_CALENDAR_SETUP_COMPLETE,
  SETTINGS,
} from '../common/constants/routingConstants'
import { getClientSessionsRemaining, hasRole, isEngageDAProvider } from '../common/utils/utils'
import { useNavigateToCloseSession } from '../dashboard/openItems/hooks/useNavigateToCloseSession'
import {
  getAuthConfig,
  getAuthInPersonPreference,
  getAuthIsLTSupervisor,
  getAuthMosApproved,
  getAuthRoles,
  getAuthUser,
  getAuthUserCapacityValue,
  getAuthUserId,
  getAuthVideoPreference,
} from '../data/auth/authSelectors'
import { setSelectedTimeZone } from '../data/authActions'
import { getRequestPaymentSelectedProvider } from '../data/requestPayment/requestPaymentSelectors'
import { useAppDispatch } from '../data/store'
import { useLazyGetAssignmentsQuery } from '../lyraTherapy/assignments/data/assignmentsApi'
import { useGetNotesQuery } from '../lyraTherapy/clientNotes/data/notesApi'
import {
  toBulkUpdatePayload,
  useCancelAppointmentMutation,
  useLazyGetAppointmentsForPatientQuery,
  useLazyGetAppointmentsQuery,
  useUpdateAppointmentMutation,
  useUpdateAppointmentsBulkMutation,
} from '../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { setAppointment } from '../lyraTherapy/clients/clientDetails/data/appointmentsSlice'
import { useBookAppointment } from '../lyraTherapy/clients/clientDetails/data/hooks/useBookAppointment'
import { useBookLegacyAppointment } from '../lyraTherapy/clients/clientDetails/data/hooks/useBookLegacyAppointment'
import { useLazyGetClientQuery } from '../lyraTherapy/clients/clientDetails/data/ltClientApi'
import { setSelectedClientId } from '../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useCalendarClients } from '../lyraTherapy/clients/data/hooks/useCalendarClients'
import { useLegacyCalendarClients } from '../lyraTherapy/clients/data/hooks/useLegacyCalendarClients'
import { setToastContent } from '../lyraTherapy/data/ltToastAutoActions'
import {
  useLazyGetAllClientEpisodesQuery,
  useLazyGetClientEpisodesQuery,
} from '../lyraTherapy/episodes/data/episodesApi'
import { updateProviderCapacity, updateSelectedProviderCapacity } from '../providers/data/providersDataActions'
import { getProviderDetailsAddresses, getProviderDetailsData } from '../providers/data/providerSelectors'
import { getProvider } from '../providers/individualProvider/data/providerDetailsActions'
import { useGetVideoSessionPreferenceQuery } from '../settings/settingsApi'

const SetupCard = styled(CalendarSetupCard)(({ theme }) => ({
  paddingTop: theme.spacing['72px'],
}))

const CalendarContainer = styledWeb.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: '#F7F6F5',
})

type CurrentCalendarLocationState = {
  daInvoiceFailedModal?: { show?: boolean; clientName?: string; sessionNumber?: string | number }
}

const formatAvailabilityModality = (modality?: EventModality | null) => {
  if (!modality) return ''
  const parts: string[] = []
  if (modality.video.available) {
    parts.push(modality.video.platform ? `Video - ${VIDEO_PLATFORM_DISPLAY_NAME[modality.video.platform]}` : 'Video')
  }
  if (modality.in_person.available) {
    parts.push('In-person')
  }
  return parts.join(', ')
}

const formatAppointmentModality = (format: string, platform?: VIDEO_PLATFORM) =>
  format === MEETING_FORMATS.VIDEO && platform
    ? `Video - ${VIDEO_PLATFORM_DISPLAY_NAME[platform]}`
    : format === MEETING_FORMATS.VIDEO
      ? 'Video'
      : format === MEETING_FORMATS.IN_PERSON
        ? 'In-person'
        : format

const findDbtTeensEpisode = (episodes: Episode[], episodeId?: string | null) =>
  episodes.find(({ id }) => id === episodeId) ??
  episodes.find(
    ({ program_name, state }) =>
      program_name === ProgramNames.DialecticalBehaviorTherapyTeens && state === episodeStates.IN_PROGRESS,
  )

const CONFLICT_STRATEGY_BY_BOOKING_MODE: Partial<Record<BookingMode, ConflictStrategy>> = {
  [BookingMode.HYBRID_BLOCK]: ConflictStrategy.IGNORE_AVAILABILITY,
  [BookingMode.BOOKABLE_BLOCK]: ConflictStrategy.EVENT_MARKER_ONLY,
}

const CurrentCalendar: FunctionComponent<CurrentCalendarProps> = ({
  actions: { getProvider, setToastContent, updateProviderCapacity, updateSelectedProviderCapacity },
}) => {
  const intl = useIntl()
  const {
    isInProductCalendarEnabled,
    isInProductCalendarModalityEnabled,
    inProductCalendarRefreshIntervalMS,
    isDAProviderMigrated,
    isSelfPayEnabled,
    isBendProviderUsingCSForAvailability,
    isLegacyProviderScheduleSessionEnabled,
    isDAEngageOnOffPlatformVideoEnabled,
    isTimezoneSelectorEnabled,
    isDBTTeensEnabled,
    isNPTestingEnabled,
  } = useFlags()
  const userId = useSelector(getAuthUserId)
  const userRoles = useSelector(getAuthRoles)
  const config = useSelector(getAuthConfig)
  const openClientAccessSupported = config?.openClientAccessSupported ?? false
  const isSupervisor = useSelector(getAuthIsLTSupervisor)
  const inPersonPreference = useSelector(getAuthInPersonPreference) ?? false
  // TODO NETEN-1635: This may be switched to false by default in the future, but for now this field doesn't exist in the response
  // from PP-API so it needs to be true by default
  const videoPreference = useSelector(getAuthVideoPreference) ?? true
  // MOS-approved providers see the video modality labeled "Virtual" instead of "Video" on calendar events.
  const mosApproved = useSelector(getAuthMosApproved)
  const providerDetailsData = useSelector(getProviderDetailsData)
  const providerAddresses = useSelector(getProviderDetailsAddresses)
  const user = useSelector(getAuthUser)
  const { data: videoSessionPreference } = useGetVideoSessionPreferenceQuery(
    { id: userId },
    { skip: !isDAEngageOnOffPlatformVideoEnabled || !userId || !config?.supportsOnOffPlatformVideo },
  )
  const providerVideoPlatform = videoSessionPreference ?? null
  const timeZone = useTimeZone()
  const { data: availableCalendars } = useGetAvailableCalendarsQuery({ providerId: userId })
  const requiresAuth = useCalendarRequiresAuth()

  const [getCalendarEvents] = useLazyGetCalendarEventsQuery()
  const [patchCalendarEvent] = usePatchCalendarEventMutation()
  const [getGoogleEvent] = useLazyGetGoogleEventQuery()
  const [postCalendarEvents] = usePostCalendarEventsMutation()
  const [getBookedDetails] = useLazyGetAppointmentBookedDetailsQuery()
  const [getClient] = useLazyGetClientQuery()
  const [getAppointmentsForPatient] = useLazyGetAppointmentsForPatientQuery()
  const [getAssignments] = useLazyGetAssignmentsQuery()

  const calendarConfiguration = useCalendarConfiguration()
  const showCalendarLiveModal = useSelector(getCalendarShowCalendarLiveModal)
  const navigate = useNavigate()
  const { data: calendarProvider } = useGetCalendarProviderQuery(
    { providerId: userId },
    { refetchOnMountOrArgChange: true },
  )
  const calendarRef = useRef<ProviderCalendarRef>(null)
  const capacities = useSelector(getAuthUserCapacityValue)
  const { openUpdateFlow: openCapacityModalFromHook, modals: capacityFlowModals } = useCapacityUpdateFlow()
  const [monthCountForNotes, setMonthCountForNotes] = useState<number>(1)
  const onScheduleEntryPointRef = useRef<string | null>(null)
  const selectedProvider = useSelector(getRequestPaymentSelectedProvider)
  const bookAppointment = useBookAppointment()
  const bookLegacyAppointment = useBookLegacyAppointment()
  const isLegacyProvider = Boolean(
    isLegacyProviderScheduleSessionEnabled && config?.inProductCalendar?.showLegacyScheduleSession,
  )
  const location = useLocation()
  const locationState = (location.state ?? {}) as CurrentCalendarLocationState
  const daInvoiceState = locationState.daInvoiceFailedModal
  const [showInvoiceFailedModal, setShowInvoiceFailedModal] = useState<boolean>(!!daInvoiceState?.show)
  const [clientVoicemailConsent, setClientVoicemailConsent] = useState<boolean | undefined>(undefined)
  const [guardianVoicemailConsent, setGuardianVoicemailConsent] = useState<boolean | undefined>(undefined)
  const [guardianPhone, setGuardianPhone] = useState<string | undefined>(undefined)
  const [clientPronouns, setClientPronouns] = useState<string[] | undefined>(undefined)
  const [isClientDataLoading, setIsClientDataLoading] = useState<boolean>(false)
  const [getClientEpisodes] = useLazyGetClientEpisodesQuery()
  const [getAllClientEpisodes] = useLazyGetAllClientEpisodesQuery()

  const [getAppointments] = useLazyGetAppointmentsQuery()
  const [updateAppointment] = useUpdateAppointmentMutation()
  const [updateAppointmentsBulk] = useUpdateAppointmentsBulkMutation()
  const [cancelAppointment, { isLoading: isCancelingMosAppointment }] = useCancelAppointmentMutation()
  const [appointments, setAppointments] = useState<Appointment[]>()
  const [mosAppointmentToCancel, setMosAppointmentToCancel] = useState<number | null>(null)

  const getAppointmentBookedDetails = useCallback(
    async (appointmentId: number) => {
      const details = await getBookedDetails({ appointmentId }).unwrap()
      // MOS clients require client info (MOS ID, Auth #, Lyra Code) that the booked-details endpoint
      // does not return. Only when the customer is Military OneSource do we fetch the client record
      // and merge those fields in, so the popover can display them without a request for every client.
      // We always mark MOS details as isMos so the popover suppresses standard PII even if the
      // supplementary fields can't be fetched (failed request or unresolved client id).
      if (details?.customer === MILITARY_ONE_SOURCE_EMPLOYER) {
        const clientId = appointments?.find((appt) => appt.appointmentId === appointmentId)?.userInfo?.lyraId
        if (clientId) {
          try {
            const client = await getClient({ providerId: userId, clientId }).unwrap()
            return {
              ...details,
              isMos: true,
              mosId: client?.mos_id,
              authorizationNumber: client?.authorization_number,
              lyraCode: client?.lyra_code,
            }
          } catch {
            // Client fetch failed — fall through and still hide PII by marking the details as MOS.
          }
        }
        return { ...details, isMos: true }
      }

      const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
      const isDbtTeensAppointment =
        isDBTTeensEnabled &&
        getProgramNameFromId(appointment?.programId) === ProgramNames.DialecticalBehaviorTherapyTeens
      if (isDbtTeensAppointment) {
        const patientId = appointment?.userInfo?.lyraId
        let caregiverLyraId = appointment?.guardianLyraId ?? details.guardianLyraId
        let caregiverPhone = details.guardianPhone || appointment?.guardianPhone
        let caregiverFirstName = details.guardianFirstName || appointment?.guardianFirstName
        let caregiverLastName = details.guardianLastName || appointment?.guardianLastName
        let selectedEpisode: Episode | undefined
        if (patientId) {
          const episodes = await getClientEpisodes({ providerId: userId, patientId })
            .unwrap()
            .catch(() => [])
          selectedEpisode = findDbtTeensEpisode(episodes, appointment.episodeId)
          if (!selectedEpisode) {
            const allClientEpisodes = await getAllClientEpisodes({ patientId }, true)
              .unwrap()
              .catch(() => [])
            selectedEpisode = findDbtTeensEpisode(allClientEpisodes, appointment.episodeId)
          }
          const caregiverParticipant = selectedEpisode?.participants?.find(
            (participant) =>
              participant.role === EpisodeParticipantRole.SECONDARY_PATIENT &&
              participant.status?.toLowerCase() === 'active',
          )
          caregiverLyraId = caregiverParticipant?.participant_lyra_id ?? caregiverLyraId
          caregiverPhone = caregiverPhone || caregiverParticipant?.sms_notification_number || undefined
        }
        const selectedEpisodeId = selectedEpisode?.id ?? appointment?.episodeId ?? undefined
        if (patientId && selectedEpisodeId) {
          try {
            const episodeAppointments = await getAppointmentsForPatient({
              providerId: userId,
              patientId,
              episodeId: selectedEpisodeId,
              skipReduxDispatch: true,
            }).unwrap()
            const participantNames = getDbtTeensSchedulingParticipantNames({
              episode: selectedEpisode,
              appointments: episodeAppointments,
            })
            caregiverFirstName = participantNames.caregiverFirstName || caregiverFirstName
            caregiverLastName = participantNames.caregiverLastName || caregiverLastName
          } catch {
            // Keep booked or appointment caregiver details when episode appointments cannot be loaded.
          }
        }
        return {
          ...details,
          guardianLyraId: caregiverLyraId,
          guardianFirstName: caregiverFirstName,
          guardianLastName: caregiverLastName,
          guardianPhone: caregiverPhone,
        }
      }
      return details
    },
    [
      getBookedDetails,
      getAllClientEpisodes,
      getClient,
      getClientEpisodes,
      getAppointmentsForPatient,
      appointments,
      isDBTTeensEnabled,
      userId,
    ],
  )

  const showInactiveClients = config?.inProductCalendar?.showInactiveClientsForScheduling
  const engageCalendarClients = useCalendarClients({ enabled: !isLegacyProvider })
  const legacyCalendarClients = useLegacyCalendarClients({ enabled: isLegacyProvider })
  const { suggestedClients, clients, allClients } = isLegacyProvider ? legacyCalendarClients : engageCalendarClients
  const isLegacyClientListLoading = isLegacyProvider ? legacyCalendarClients.isLoading : undefined

  const totalClientsCount = showInactiveClients
    ? suggestedClients.length + allClients.length
    : suggestedClients.length + clients.length
  const { data: allNotes, isSuccess: notesFetched } = useGetNotesQuery(
    {
      providerId: userId,
      months: monthCountForNotes,
      note_types: [NoteTypes.INITIAL, NoteTypes.SESSION],
      excludedFields: ['body', 'content_meta_data'],
    },
    {
      skip:
        !isInProductCalendarEnabled ||
        !isEngageDAProvider(userRoles) ||
        !isDAProviderMigrated ||
        totalClientsCount === 0,
      refetchOnMountOrArgChange: true,
    },
  )

  const providerCalendarLive = useProviderCalendarLive(config, calendarProvider)
  const shouldShowModalitySection =
    isInProductCalendarModalityEnabled && !!config?.inProductCalendar?.showModalitySelection
  const dispatch = useAppDispatch()
  const isDAEngageProviderAndNotMigrated = isEngageDAProvider(userRoles) && !isDAProviderMigrated
  const isMigratedEngageDAProvider = isEngageDAProvider(userRoles) && isDAProviderMigrated
  const isCapacityRolledOut =
    isMigratedEngageDAProvider || (hasRole(userRoles, NP_TESTER_ROLES) && !!isNPTestingEnabled)
  const { closeSession, completeNote, viewCompletedNote } = useNavigateToCloseSession({
    months: monthCountForNotes,
    originRoute: 'calendar',
    skipNotes:
      !isInProductCalendarEnabled || !isEngageDAProvider(userRoles) || !isDAProviderMigrated || totalClientsCount === 0,
  })

  useFetcher([getProvider, [userId], isEmpty(providerDetailsData) && shouldShowModalitySection])

  const pollingIntervalMs = useMemo(
    () => Math.max(inProductCalendarRefreshIntervalMS, 1000 * 60 * 3),
    [inProductCalendarRefreshIntervalMS],
  )
  const refetchData = useCallback(() => {
    if (window.Cypress) {
      return
    }
    calendarRef.current?.refresh()
  }, [])

  useVisibleInterval(refetchData, { intervalMs: pollingIntervalMs })

  const updateCapacity = useCallback(
    async (value: number) => {
      if (!userId) {
        return
      }
      const updateCapacity = isEmpty(selectedProvider) ? updateProviderCapacity : updateSelectedProviderCapacity
      const newCapacities = { ...capacities }
      if ('BlendedCareTherapy' in newCapacities && newCapacities.BlendedCareTherapy != null) {
        newCapacities.BlendedCareTherapy += value
      }
      return updateCapacity({
        id: userId,
        data: { capacity: newCapacities },
      })
    },
    [capacities, selectedProvider, updateProviderCapacity, updateSelectedProviderCapacity, userId],
  )

  const onDeleteOverlappingEvents = useCallback(
    async (overlappingSlots: string[]) => {
      if (!calendarConfiguration?.id) {
        return Promise.reject(new Error('Cannot delete overlapping events: no calendar configuration is loaded'))
      }
      await Promise.all(
        overlappingSlots.map((externalId) =>
          patchCalendarEvent({
            external_id: externalId,
            body: {
              provider_calendar_configuration_id: calendarConfiguration.id,
              event: {
                status: 'cancelled',
              },
            },
          }).unwrap(),
        ),
      )
      calendarRef.current?.refresh()
    },
    [calendarConfiguration?.id, patchCalendarEvent],
  )

  const getAlerts = useCalendarAlerts(
    updateCapacity,
    setToastContent,
    openCapacityModalFromHook,
    onDeleteOverlappingEvents,
  )

  // For Core provider booking modes, show bookable events even if is_blocked
  // This allows availability to be visible with conflicts displayed on top
  const shouldOverlapAvailability = [BookingMode.BOOKABLE_BLOCK, BookingMode.HYBRID_BLOCK].includes(
    calendarProvider?.booking_mode as BookingMode,
  )

  const conflictStrategy =
    CONFLICT_STRATEGY_BY_BOOKING_MODE[calendarProvider?.booking_mode as BookingMode] ?? ConflictStrategy.DEFAULT
  const conflictTooltipText =
    calendarProvider?.booking_mode === BookingMode.BOOKABLE_BLOCK
      ? intl.formatMessage({
          defaultMessage: 'You are still available to book at this time',
          description: 'Tooltip for conflict badge on events overlapping with availability slots',
        })
      : undefined

  const mapCalendarEvent = useCallback(
    (
      event: ProviderCalendarEvent,
      appointmentsByIdMap: { [key: number]: Appointment },
      appointmentsByEventIdMap: { [key: string]: Appointment },
    ) => {
      const calendar_name =
        availableCalendars?.find((calendarConfig) => {
          return event.provider_calendar_configuration_id === calendarConfig.id
        })?.calendar_name ?? ''
      const extendedProps = mapExtendedProps(
        event,
        appointmentsByIdMap,
        calendar_name,
        appointmentsByEventIdMap,
        shouldShowModalitySection,
      )
      return {
        id: event.id,
        title: event.title,
        start: toUTCISOString(event.start_datetimetz, timeZone),
        end: toUTCISOString(event.end_datetimetz, timeZone),
        startEditable: isBookableEvent(extendedProps),
        extendedProps,
        ...(isBookableEvent(extendedProps) && shouldOverlapAvailability && { display: 'background' }),
      }
    },
    [availableCalendars, shouldShowModalitySection, shouldOverlapAvailability, timeZone],
  )

  // Builds lookup maps from an appointments array (used when flag is on)
  const buildAppointmentMaps = useCallback((appointments: Appointment[]) => {
    const byId = appointments.reduce(
      (acc, curr) => {
        acc[curr.appointmentId] = curr
        return acc
      },
      {} as { [key: number]: Appointment },
    )
    const byEventId = appointments.reduce(
      (acc, curr) => {
        acc[curr.eventId] = curr
        return acc
      },
      {} as { [key: string]: Appointment },
    )
    return { byId, byEventId }
  }, [])

  const getEventsForRange = useCallback(
    async ({
      startDate,
      endDate,
      isLoadingPrimaryCalendar,
    }: {
      startDate: string
      endDate: string
      /** When true, persists fetched appointments to state for use by other callbacks */
      isLoadingPrimaryCalendar?: boolean
    }) => {
      if (!userId || availableCalendars?.length === 0) {
        return []
      }

      const localStartDate = toLocalISOString(startDate, timeZone)
      const localEndDate = toLocalISOString(endDate, timeZone)
      const appointmentsPromise = getAppointments({
        providerId: userId,
        view: CalendarAppointmentsView.CALENDAR,
        dateRange: { start: localStartDate, end: localEndDate },
      }).unwrap()

      const monthsSinceStartDate = differenceInMonths(new Date(), new Date(localStartDate)) + 1
      if (monthsSinceStartDate > monthCountForNotes) {
        setMonthCountForNotes(monthsSinceStartDate)
      }
      const eventsPromise = getCalendarEvents({
        startDate: localStartDate,
        endDate: localEndDate,
        providerId: userId,
      }).unwrap()

      const [fetchedAppointments, events] = await Promise.all([appointmentsPromise, eventsPromise])

      const maps = buildAppointmentMaps(fetchedAppointments)

      // Only persist to state when this is the primary calendar load (not ad-hoc queries like slot checks)
      if (isLoadingPrimaryCalendar) {
        setAppointments(fetchedAppointments)
      }

      const shouldFilterOutEvent = (event: ProviderCalendarEvent) => {
        // Always filter out recurring bookable parent events (we show instances instead)
        if (event.event_type === 'bookable_recurring') {
          return true
        }
        // For block booking modes, allow bookable events through even if is_blocked
        if (shouldOverlapAvailability) {
          const isBookable = event.event_type === 'bookable'
          return event.is_blocked && !isBookable
        }
        // Original behavior for other modes
        return event.is_blocked
      }

      if (!isDAProviderMigrated || !config?.inProductCalendar?.showLateCancellations) {
        return events
          .filter((event) => !shouldFilterOutEvent(event))
          .map((event) => mapCalendarEvent(event, maps.byId, maps.byEventId))
      }

      // Get late-cancelled appointments to display as synthetic events
      const lateCancelledAppointments = filterLateCancelledAppointments(
        fetchedAppointments,
        localStartDate,
        localEndDate,
      )

      // Filter and map calendar events, removing availability blocks that overlap with late-cancelled sessions
      const calendarEvents = events
        .filter((event) => {
          if (shouldFilterOutEvent(event)) {
            return false
          }
          if (eventOverlapsWithLateCancellations(event, lateCancelledAppointments)) {
            return false
          }
          return true
        })
        .map((event) => mapCalendarEvent(event, maps.byId, maps.byEventId))

      // Get the appointment IDs that already have calendar events
      const existingAppointmentIds = new Set(
        calendarEvents
          .filter((event) => (event.extendedProps as EventExtendedProps)?.lyraEventType === 'session')
          .map(
            (event) => (event.extendedProps as EventExtendedProps & { lyraAppointmentId?: number })?.lyraAppointmentId,
          )
          .filter(Boolean),
      )

      // Filter out late-cancelled appointments that already have calendar events
      const lateCancelledAppointmentsWithoutEvents = lateCancelledAppointments.filter(
        (appt) => !existingAppointmentIds.has(appt.appointmentId),
      )

      const lateCancelledAppointmentEvents = lateCancelledAppointmentsWithoutEvents.map((appt) =>
        createSyntheticEventFromCancelledAppointment(appt, timeZone),
      )

      return [...calendarEvents, ...lateCancelledAppointmentEvents]
    },
    [
      availableCalendars?.length,
      buildAppointmentMaps,
      shouldOverlapAvailability,
      config?.inProductCalendar?.showLateCancellations,
      isDAProviderMigrated,
      getAppointments,
      getCalendarEvents,
      mapCalendarEvent,
      monthCountForNotes,
      timeZone,
      userId,
    ],
  )

  useEffect(() => {
    if (appointments != null && appointments.length > 0) {
      const extendedPropsMap = appointments.reduce(
        (acc, curr) => {
          acc[curr.appointmentId] = getSessionExtendedProperties(curr)
          return acc
        },
        {} as { [key: number]: LyraEventExtendedPropsNoTZ },
      )
      calendarRef.current?.updateAppointments(extendedPropsMap)
    }
  }, [appointments])

  const onEventMoved = useCallback(
    (
      event: {
        externalId: string
        startTime: string
        endTime: string
        modality?: EventModality | null
        oldModality?: EventModality | null
        title?: string
        booking_type?: string[] | null
      },
      revert: () => void,
    ) => {
      if (!calendarConfiguration?.id) {
        revert()
        return
      }
      track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_MANAGEMENT_EDIT_AVAILABLE })
      if (!isSameModality(event.modality, event.oldModality)) {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: mixpanelActions.CALENDAR_AVAILABILITY_MODALITY_CHANGED,
          details: {
            availability_id: event.externalId,
            old_modality: formatAvailabilityModality(event.oldModality),
            new_modality: formatAvailabilityModality(event.modality),
          },
        })
      }
      patchCalendarEvent({
        external_id: event.externalId,
        body: {
          provider_calendar_configuration_id: calendarConfiguration.id,
          event: {
            title: event.title,
            start: event.startTime,
            end: event.endTime,
            modality_availability: event.modality,
            booking_type: event.booking_type,
          },
        },
      })
        .unwrap()
        .then(() => {
          setToastContent({
            text: 'Availability updated this week',
            toastType: 'success',
            id: 'CurrentCalendar-move-success',
          })
        })
        .catch(() => {
          setToastContent({
            text: 'Failed to update availability',
            toastType: 'error',
            id: 'CurrentCalendar-move-error',
          })
          revert()
        })
    },
    [calendarConfiguration, patchCalendarEvent, setToastContent],
  )

  const onEventsAdded = useCallback(
    (
      events: {
        startTime: string
        endTime: string
        modality?: EventModality | null
        title?: string | null
        booking_type?: string[] | null
      }[],
      revert: () => void,
      attachEventID: (events: { id: string }[]) => void,
      entryPoint: 'calendar' | 'add button' | null,
    ) => {
      if (!calendarConfiguration?.id) {
        revert()
        return
      }
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: mixpanelActions.CALENDAR_MANAGEMENT_ADD_AVAILABLE,
        details: {
          [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: entryPoint,
          slot_list: events.map((event) => event.startTime),
        },
      })
      postCalendarEvents({
        provider_calendar_configuration_id: calendarConfiguration?.id,
        events: events.map((event) =>
          mapEventToBookableEvent(event, timeZone, false, config?.inProductCalendar?.hybridBookingMode),
        ),
      })
        .unwrap()
        .then((events) => {
          setToastContent({
            text: 'Availability added this week',
            toastType: 'success',
            id: 'CurrentCalendar-add-success',
          })
          attachEventID(events)
        })
        .catch(() => {
          setToastContent({
            text: 'Failed to add availability',
            toastType: 'error',
            id: 'CurrentCalendar-add-error',
          })
          revert()
        })
    },
    [calendarConfiguration?.id, config, postCalendarEvents, setToastContent, timeZone],
  )

  const onEventDeleted = useCallback(
    (event: { externalId: string }, revert: () => void) => {
      if (!calendarConfiguration?.id) {
        revert()
        return
      }
      track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_MANAGEMENT_DELETE_AVAILABLE })
      patchCalendarEvent({
        external_id: event.externalId,
        body: {
          provider_calendar_configuration_id: calendarConfiguration.id,
          event: {
            status: 'cancelled',
          },
        },
      })
        .unwrap()
        .then(() => {
          setToastContent({
            text: 'Availability deleted this week',
            toastType: 'success',
            id: 'CurrentCalendar-delete-success',
          })
        })
        .catch(() => {
          setToastContent({
            text: 'Failed to delete availability',
            toastType: 'error',
            id: 'CurrentCalendar-delete-error',
          })
          revert()
        })
    },
    [calendarConfiguration, patchCalendarEvent, setToastContent],
  )

  const getOrFetchGoogleEvent = useCallback(
    async (externalId: string, calendarConfigurationId: string) => {
      const event = await getGoogleEvent({
        external_id: externalId,
        provider_calendar_configuration_id: calendarConfigurationId,
      }).unwrap()
      return event
    },
    [getGoogleEvent],
  )

  const onOpenInGoogleCalendar = useCallback(
    (externalId: string, calendarConfigurationId: string) => {
      track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_MANAGEMENT_OPEN_IN_GOOGLE_CALENDAR })
      getOrFetchGoogleEvent(externalId, calendarConfigurationId)
        .then((event) => {
          window.open(event.htmlLink, '_blank')
        })
        .catch(() => {
          setToastContent({
            text: 'Failed to open google calendar',
            toastType: 'error',
            id: 'CurrentCalendar-open-google-error',
          })
        })
    },
    [getOrFetchGoogleEvent, setToastContent],
  )

  const onOpenVideoConferencing = useCallback(
    (externalId: string, calendarConfigurationId: string) => {
      track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_MANAGEMENT_JOIN_MEETING })
      getOrFetchGoogleEvent(externalId, calendarConfigurationId)
        .then((event) => {
          const zoomUrl = event.conferenceData?.entryPoints.find(
            (entryPoint) => entryPoint.entryPointType === 'video',
          )?.uri
          if (zoomUrl) {
            window.open(zoomUrl, '_blank')
          } else {
            setToastContent({
              text: 'Failed to open zoom',
              toastType: 'error',
              id: 'CurrentCalendar-open-zoom-error',
            })
          }
        })
        .catch(() => {
          setToastContent({
            text: 'Failed to open zoom',
            toastType: 'error',
            id: 'CurrentCalendar-open-zoom-error',
          })
        })
    },
    [getOrFetchGoogleEvent, setToastContent],
  )

  const onOpenPopover = useCallback(
    async ({ appointmentId, clientId }: { appointmentId: number; clientId: string }) => {
      if (clientId) {
        setIsClientDataLoading(true)
        setClientVoicemailConsent(undefined)
        setGuardianVoicemailConsent(undefined)
        setGuardianPhone(undefined)
        setClientPronouns(undefined)
        await dispatch(setSelectedClientId(clientId))
        const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
        if (appointment) {
          await dispatch(setAppointment(appointment))
        }
        try {
          const client = await getClient({ providerId: userId, clientId }).unwrap()
          setClientVoicemailConsent(client?.voicemail_consent ?? false)
          setClientPronouns(client?.pronouns)
          if (appointment?.guardianLyraId && appointment?.episodeId) {
            const episodes = await getClientEpisodes({ providerId: userId, patientId: clientId }).unwrap()
            const episode = episodes?.find((ep: Episode) => ep.id === appointment.episodeId)
            const guardianParticipant = episode?.participants?.find(
              (p) => p.role === EpisodeParticipantRole.PRIMARY_GUARDIAN,
            )
            setGuardianVoicemailConsent(guardianParticipant?.voicemail_consent ?? false)
            setGuardianPhone(guardianParticipant?.sms_notification_number ?? undefined)
          }
        } catch {
          setClientVoicemailConsent(false)
          setGuardianVoicemailConsent(false)
          setClientPronouns(undefined)
        } finally {
          setIsClientDataLoading(false)
        }
      }
    },
    [appointments, dispatch, getClient, getClientEpisodes, userId],
  )

  const onCancelPressed = useCallback(
    async (appointmentId: number) => {
      const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
      if (appointment) {
        track({
          event: mixpanelEvents.CANCEL_SESSION,
          details: {
            appointmentId: appointment.appointmentId,
            entryPoint: 'In Product Calendar',
          },
        })
        await dispatch(setAppointment(appointment))
        navigate(CLIENTS_CANCEL_SESSION.route)
      }
    },
    [appointments, navigate, dispatch],
  )

  const onCancelMosAppointmentPressed = useCallback(
    (appointmentId: number) => {
      const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
      if (!appointment) {
        return
      }
      track({
        event: mixpanelEvents.CANCEL_SESSION,
        details: {
          appointmentId: appointment.appointmentId,
          entryPoint: 'In Product Calendar',
        },
      })
      setMosAppointmentToCancel(appointmentId)
    },
    [appointments],
  )

  const confirmCancelMosAppointment = useCallback(async () => {
    if (mosAppointmentToCancel == null) {
      return
    }

    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: mixpanelActions.CANCEL_SESSION,
      details: {
        appointmentId: mosAppointmentToCancel,
        entryPoint: 'In Product Calendar',
      },
    })
    try {
      await cancelAppointment({ providerId: userId, appointmentId: mosAppointmentToCancel }).unwrap()
      setMosAppointmentToCancel(null)
      calendarRef.current?.refresh()
      setToastContent({
        text: intl.formatMessage({
          defaultMessage: 'Appointment canceled',
          description: 'Success message after canceling a MOS appointment from the provider calendar',
        }),
        toastType: 'success',
        id: 'CurrentCalendar-cancel-mos-appointment-success',
      })
    } catch {
      setToastContent({
        text: intl.formatMessage({
          defaultMessage: "We couldn't cancel the appointment. Try again.",
          description: 'Error message after failing to cancel a MOS appointment from the provider calendar',
        }),
        toastType: 'error',
        id: 'CurrentCalendar-cancel-mos-appointment-error',
      })
    }
  }, [cancelAppointment, intl, mosAppointmentToCancel, setToastContent, userId])

  const onReschedule = useCallback(
    async (appointmentId: number) => {
      const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
      if (appointment) {
        track({
          event: mixpanelEvents.RESCHEDULE_SESSION,
          details: {
            appointmentId: appointment.appointmentId,
            entryPoint: 'In Product Calendar',
          },
        })
        await dispatch(setAppointment(appointment))
        navigate(CLIENTS_RESCHEDULE_SESSION_DETAILS.route, { state: { entryPoint: 'In Product Calendar' } })
      }
    },
    [appointments, dispatch, navigate],
  )

  const onCloseSession = useCallback(
    async (appointmentId: number, clientId: string) => {
      const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
      if (appointment) {
        track({
          event: mixpanelEvents.CLOSE_SESSION,
          details: {
            appointmentId: appointment.appointmentId,
            entryPoint: 'In Product Calendar',
          },
        })
        await closeSession(appointment, clientId)
      }
    },
    [appointments, closeSession],
  )

  const onViewCompletedNote = useCallback(
    async (appointmentId: number) => {
      const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
      if (appointment) {
        await viewCompletedNote(appointment)
      }
    },
    [appointments, viewCompletedNote],
  )

  const onCompleteNote = useCallback(
    async (appointmentId: number, clientId: string) => {
      const appointment = appointments?.find((appt) => appt.appointmentId === appointmentId)
      if (appointment) {
        await completeNote(appointment, clientId)
      }
    },
    [appointments, completeNote],
  )

  const getUpcomingAppointmentIds = useCallback(
    async (clientId: string): Promise<number[]> => {
      try {
        const allAppointments = await getAppointmentsForPatient({
          providerId: userId,
          patientId: clientId,
          skipReduxDispatch: true,
        }).unwrap()
        const upcomingAppointments = allAppointments
          .filter(
            (appt) =>
              !isPast(getDateFromAppointment(appt)) &&
              [APPOINTMENT_STATUS.NEW_APPT, APPOINTMENT_STATUS.RESCHEDULED].includes(
                appt.appointmentStatus as APPOINTMENT_STATUS,
              ),
          )
          .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
        setAppointments((prev) => {
          const existingIds = new Set((prev ?? []).map((appt) => appt.appointmentId))
          const additions = upcomingAppointments.filter((appt) => !existingIds.has(appt.appointmentId))
          return additions.length > 0 ? [...(prev ?? []), ...additions] : prev
        })
        return upcomingAppointments.map((appt) => appt.appointmentId)
      } catch {
        return []
      }
    },
    [getAppointmentsForPatient, userId],
  )

  const onUpdateModality = useCallback(
    async (
      appointmentIdsToUpdate: number[],
      meetingFormat: MEETING_FORMATS,
      address?: string,
      videoPlatform?: VIDEO_PLATFORM,
    ) => {
      const resolvedVideoPlatform = config?.supportsOnOffPlatformVideo ? videoPlatform : undefined
      try {
        const appointmentMap = new Map((appointments ?? []).map((a) => [a.appointmentId, a]))
        const newModality = formatAppointmentModality(meetingFormat, resolvedVideoPlatform ?? videoPlatform)
        appointmentIdsToUpdate.forEach((id) => {
          const appt = appointmentMap.get(id)
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.CALENDAR_APPOINTMENT_MODALITY_CHANGED,
            details: {
              appointment_id: id,
              old_modality: formatAppointmentModality(appt?.meetingFormat ?? '', appt?.videoPlatform),
              new_modality: newModality,
            },
          })
        })
        const modalityOverrides = {
          meetingFormat,
          address: address ?? '',
          ...(resolvedVideoPlatform !== undefined && { videoPlatform: resolvedVideoPlatform }),
        }
        let scheduledAppointmentIds: number[]
        if (appointmentIdsToUpdate.length === 1) {
          const appt = appointmentMap.get(appointmentIdsToUpdate[0])
          if (appt) {
            await updateAppointment({
              providerId: userId,
              data: { ...appt, ...modalityOverrides },
            }).unwrap()
          }
          scheduledAppointmentIds = appt ? [appt.appointmentId] : []
        } else {
          const bulkResponse = await updateAppointmentsBulk({
            providerId: userId,
            data: toBulkUpdatePayload(appointments ?? [], appointmentIdsToUpdate, {
              meetingFormat,
              address: address ?? '',
              videoPlatform: resolvedVideoPlatform,
            }),
          }).unwrap()
          const scheduledIds = new Set(bulkResponse.scheduled)
          scheduledAppointmentIds = appointmentIdsToUpdate.filter((id) => scheduledIds.has(id))
        }
        // Build the updated extended props map and push directly to the calendar so that
        // the event tile and open popover reflect the new modality immediately, without
        // relying on the useEffect → setTimeout(0) chain which can race with a refetch.
        const extendedPropsMap: { [key: number]: LyraEventExtendedPropsNoTZ } = {}
        scheduledAppointmentIds.forEach((appointmentId) => {
          const appointment = appointmentMap.get(appointmentId)
          if (appointment) {
            extendedPropsMap[appointmentId] = getSessionExtendedProperties({
              ...appointment,
              meetingFormat,
              address: address ?? '',
              ...(resolvedVideoPlatform !== undefined && { videoPlatform: resolvedVideoPlatform }),
            })
          }
        })
        calendarRef.current?.updateAppointments(extendedPropsMap)
        setAppointments((prev) =>
          prev?.map((appt) => {
            if (!scheduledAppointmentIds.includes(appt.appointmentId)) {
              return appt
            }
            return {
              ...appt,
              meetingFormat,
              address: address ?? '',
              ...(resolvedVideoPlatform !== undefined && { videoPlatform: resolvedVideoPlatform }),
            }
          }),
        )
        const firstAppointment = appointmentMap.get(appointmentIdsToUpdate[0])
        const firstName = firstAppointment?.userInfo?.firstName
        const modalityLabel = meetingFormat === MEETING_FORMATS.VIDEO ? 'Video' : address ? address : 'In-person'
        const now = new Date()
        const anyWithin72Hours = scheduledAppointmentIds.some((id) => {
          const appt = appointmentMap.get(id)
          if (!appt) return false
          const apptTime = new Date(`${appt.startDate} ${appt.startTime}`)
          return apptTime > now && differenceInHours(apptTime, now) <= 72
        })
        setToastContent({
          text: `Session modality updated to ${modalityLabel}.${
            anyWithin72Hours && firstName ? ` We've emailed ${firstName} this update.` : ''
          }`,
          toastType: 'success',
          id: 'CurrentCalendar-update-modality-success',
        })
      } catch (e) {
        setToastContent({
          text: 'Failed to update session modality',
          toastType: 'error',
          id: 'CurrentCalendar-update-modality-error',
        })
        throw e
      }
    },
    [
      appointments,
      config?.supportsOnOffPlatformVideo,
      setToastContent,
      updateAppointment,
      updateAppointmentsBulk,
      userId,
    ],
  )

  const getSessionsAwaitingSubmissionCount = useCallback(
    (pastSessionApptIds: number[]): number | null => {
      if (totalClientsCount === 0) {
        return 0
      }
      if (
        !isEngageDAProvider(userRoles) ||
        !isDAProviderMigrated ||
        !appointments ||
        !allNotes ||
        !pastSessionApptIds
      ) {
        return null
      }

      return pastSessionApptIds.filter((appointmentId) => {
        const appointment = appointments.find((appt) => appt.appointmentId === appointmentId)

        if (!appointment) {
          return false
        }

        const programName = getProgramNameFromId(appointment.programId)
        if (!programName || !isDAEngageProgram(programName)) {
          return false
        }

        const hasNotSubmittedInvoice =
          appointment.appointmentStatus === APPOINTMENT_STATUS.NEW_APPT ||
          appointment.appointmentStatus === APPOINTMENT_STATUS.RESCHEDULED

        const hasCompletedNote = allNotes.some(
          (note) => note.appointment_id === appointmentId && note.status === 'completed',
        )

        return (
          hasNotSubmittedInvoice ||
          (appointment.appointmentStatus === APPOINTMENT_STATUS.COMPLETED && !hasCompletedNote)
        )
      }).length
    },
    [userRoles, isDAProviderMigrated, appointments, allNotes, totalClientsCount],
  )

  const getBookingDetailsForClient = useCallback(
    async (clientId: string) => {
      try {
        const client = await getClient({ providerId: userId, clientId }).unwrap()
        const providerEpisodes = await getClientEpisodes({ providerId: userId, patientId: clientId }).unwrap()
        const liveMessagingEnabled = client?.live_messaging ?? false
        const isLiveMessagingEnabled = (liveMessagingEnabled && user?.text_sessions_enabled) || false
        const exclusions = {
          exclusions: [
            ...(!isEngageDAProvider(userRoles) || videoPreference ? [] : [MEETING_FORMATS.VIDEO]),
            ...(isLiveMessagingEnabled ? [] : [MEETING_FORMATS.LIVE_MESSAGING]),
            ...(inPersonPreference ? [] : [MEETING_FORMATS.IN_PERSON]),
            ...(isEngageDAProvider(userRoles) ? [MEETING_FORMATS.PHONE] : []),
          ],
        }
        // if live messaging is not enabled, filter out live_messaging as an option
        const enabledMeetingFormats = getMeetingFormats(exclusions)
        const activeProviderEpisode = providerEpisodes.find(
          (episode: Episode) => episode.state === episodeStates.IN_PROGRESS,
        )
        const allClientEpisodes =
          !activeProviderEpisode && isDBTTeensEnabled && openClientAccessSupported
            ? await getAllClientEpisodes({ patientId: clientId }).unwrap()
            : []
        const activeOpenClientEpisode = allClientEpisodes.find(
          (episode) =>
            episode.state === episodeStates.IN_PROGRESS &&
            episode.program_name === ProgramNames.DialecticalBehaviorTherapyTeens,
        )
        const selectedEpisode =
          activeProviderEpisode ||
          activeOpenClientEpisode ||
          providerEpisodes.toSorted((a, b) =>
            isBefore(parseISO(get(a, 'end_date') as string), parseISO(get(b, 'end_date') as string)) ? 1 : -1,
          )?.[0]
        const config = selectedEpisode
          ? programConfig[selectedEpisode.program_name as keyof typeof programConfig]
          : undefined
        const sessionsRemaining = getClientSessionsRemaining(client, selectedEpisode?.program_name)
        let sessionTypeOptions

        if (isDBTTeensEnabled && selectedEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens) {
          const [episodeAppointmentsResult, assignments] = await Promise.all([
            getAppointmentsForPatient({
              providerId: userId,
              patientId: clientId,
              episodeId: selectedEpisode.id,
              skipReduxDispatch: true,
            })
              .unwrap()
              .catch(() => undefined),
            getAssignments({ providerId: selectedEpisode.provider_id, patientId: clientId })
              .unwrap()
              .catch(() => []),
          ])
          const episodeAppointments = episodeAppointmentsResult ?? []
          const participantNames = getDbtTeensSchedulingParticipantNames({
            client,
            episode: selectedEpisode,
            appointments: episodeAppointments,
            assignments,
          })
          sessionTypeOptions = getDbtTeensSchedulingSessionTypeOptions({
            ...participantNames,
            // episodeId is passed even though the request above is already scoped to it: both this
            // and getDbtTeensSchedulingParticipantNames re-filter client-side so the derived values
            // stay episode-correct if the endpoint ever stops honoring the param.
            sessionNumber: episodeAppointmentsResult
              ? getNextDbtTeensSessionNumber({
                  appointments: episodeAppointments,
                  episodeId: selectedEpisode.id,
                })
              : undefined,
          })
        }

        const { variant: hpiSelfPayBannerVariant, isHpiOnlyCoverage } = await getHPISelfPayBannerVariantForClient({
          client,
          programName: selectedEpisode?.program_name,
          isSelfPayEnabled,
          dispatch,
        })

        return {
          meetingFormats: enabledMeetingFormats,
          sessionDuration: config?.defaultAppointmentDuration ?? 50,
          episode: selectedEpisode,
          frequencyOptions: config?.sessionFrequencyOptions?.filter((option) => option.value !== 0) ?? null,
          clientDetails: client,
          isSelfPayAvailable: Programs.getProgramConfigByName(selectedEpisode?.program_name)?.selfPayEligible ?? false,
          sessionsRemaining,
          hpiSelfPayBannerVariant,
          isHpiOnlyCoverage,
          ...(sessionTypeOptions && { sessionTypeOptions }),
        }
      } catch (error) {
        setToastContent({
          text: 'Failed to load client details',
          toastType: 'error',
          id: 'CurrentCalendar-client-details-error',
        })

        throw error
      }
    },
    [
      dispatch,
      getAllClientEpisodes,
      getAppointmentsForPatient,
      getAssignments,
      getClient,
      getClientEpisodes,
      inPersonPreference,
      isDBTTeensEnabled,
      isSelfPayEnabled,
      openClientAccessSupported,
      setToastContent,
      user?.text_sessions_enabled,
      userId,
      userRoles,
      videoPreference,
    ],
  )

  const handleTimeZoneChange = useCallback((tz: string) => dispatch(setSelectedTimeZone(tz)), [dispatch])

  const onScheduleSession = useCallback(
    async (event: {
      client: string
      startTime: string
      endTime: string
      slotEndTime: string
      localStartTime: string
      localEndTime: string
      localSlotEndTime: string
      duration: number
      meetingFormat: string
      episode?: Episode
      recurring: boolean
      frequency: number
      repeatFor: number
      address?: string
      videoPlatform?: VIDEO_PLATFORM
      sessionType?: string
    }) => {
      if (!calendarConfiguration?.id) {
        return
      }
      const { startTime, slotEndTime, localStartTime, localSlotEndTime } = event
      const currentSlots = await getEventsForRange({ startDate: startTime, endDate: slotEndTime })
      const hasMatchingSlot = currentSlots
        .filter((event) => isBookableEvent(event.extendedProps as EventExtendedProps))
        .some((slot) => {
          return slot.start === startTime && slot.end === slotEndTime
        })
      if (!hasMatchingSlot) {
        await postCalendarEvents({
          provider_calendar_configuration_id: calendarConfiguration?.id,
          events: [
            mapEventToBookableEvent(
              { startTime: localStartTime, endTime: localSlotEndTime },
              timeZone,
              false,
              config?.inProductCalendar?.hybridBookingMode,
            ),
          ],
        }).unwrap()
      }

      try {
        track({
          event: mixpanelEvents.IPC_CONFIRM_SCHEDULE_NEW_SESSION,
          details: {
            entryPoint: onScheduleEntryPointRef.current ?? '',
          },
        })
        if (isDAEngageOnOffPlatformVideoEnabled && config?.supportsOnOffPlatformVideo) {
          const defaultPlatform = videoSessionPreference ?? VIDEO_PLATFORM.EXTERNAL
          const defaultModality = formatAppointmentModality(MEETING_FORMATS.VIDEO, defaultPlatform)
          const selectedModality = formatAppointmentModality(event.meetingFormat, event.videoPlatform)
          if (selectedModality !== defaultModality) {
            track({
              event: mixpanelEvents.BUTTON_PRESS,
              action: mixpanelActions.CALENDAR_APPOINTMENT_BOOKING_NON_DEFAULT_MODALITY_SELECTED,
              details: {
                default_modality: defaultModality,
                selected_modality: selectedModality,
              },
            })
          }
        }
        if (isLegacyProvider) {
          await bookLegacyAppointment({
            client: event.client,
            localStartTime: event.localStartTime,
            duration: event.duration,
            meetingFormat: event.meetingFormat,
            address: event.address,
          })
        } else if (event.episode) {
          const episodeAppointments = await getAppointmentsForPatient({
            providerId: userId,
            patientId: event.client,
            episodeId: event.episode.id,
            skipReduxDispatch: true,
          }).unwrap()
          await bookAppointment({ ...event, episode: event.episode }, episodeAppointments)
        } else {
          throw new Error('Cannot schedule session: missing episode for engage provider')
        }
        calendarRef.current?.refresh()
        setToastContent({
          text: 'Session scheduled!',
          toastType: 'success',
          id: 'CurrentCalendar-schedule-success',
        })
      } catch {
        setToastContent({
          text: 'Failed to schedule session',
          toastType: 'error',
          id: 'CurrentCalendar-schedule-error',
        })
      }
    },
    [
      calendarConfiguration?.id,
      getEventsForRange,
      postCalendarEvents,
      timeZone,
      config?.inProductCalendar?.hybridBookingMode,
      getAppointmentsForPatient,
      userId,
      bookAppointment,
      bookLegacyAppointment,
      isLegacyProvider,
      setToastContent,
      videoSessionPreference,
      isDAEngageOnOffPlatformVideoEnabled,
      config?.supportsOnOffPlatformVideo,
    ],
  )

  const trackScheduleNewSessionEntryPoint = useCallback((entryPoint: string | null) => {
    onScheduleEntryPointRef.current = entryPoint
  }, [])

  // BEND providers with simplified setup flow done always redirect to setup-complete
  // unless they are in the isBendProviderUsingCSForAvailability feature flag
  // Supervisors can bypass the redirect to view/manage the provider's calendar
  const shouldRedirectToSetupComplete =
    config?.inProductCalendar?.simplifiedSetupFlow &&
    requiresAuth === false &&
    !isBendProviderUsingCSForAvailability &&
    !isSupervisor

  useEffect(() => {
    if (shouldRedirectToSetupComplete) {
      navigate(LC_CALENDAR_SETUP_COMPLETE.route, { replace: true })
    }
  }, [shouldRedirectToSetupComplete, navigate])

  if (!isInProductCalendarEnabled) {
    return null
  }

  if (requiresAuth == null || shouldRedirectToSetupComplete) {
    return <LoadingIndicator />
  }

  if (requiresAuth || !providerCalendarLive) {
    return config?.inProductCalendar?.hybridBookingMode ? <CalendarLandingPage /> : <SetupCard />
  }
  const backgroundClassName = shouldOverlapAvailability ? 'provider-calendar-background-availability' : ''

  return (
    <>
      <CalendarContainer className={`lc-calendar ${backgroundClassName}`} data-testid='CurrentCalendarContainer'>
        <ProviderCalendar
          ref={calendarRef}
          timeZone={timeZone}
          onTimeZoneChange={
            isTimezoneSelectorEnabled && isSupervisor && config?.inProductCalendar?.showTimezoneSelector
              ? handleTimeZoneChange
              : undefined
          }
          getEvents={getEventsForRange}
          loading={requiresAuth === undefined}
          onSettingsPressed={() => {
            track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.NAVIGATION_CALENDAR_GEAR })
            navigate(SETTINGS.route)
          }}
          onClientProfilePressed={(clientId) => {
            track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_MANAGEMENT_CLIENT_DETAILS })
            dispatch(setSelectedClientId(clientId))
            navigate({ pathname: CLIENT_HOME.route })
          }}
          onEventMoved={onEventMoved}
          onEventsAdded={onEventsAdded}
          onEventDeleted={onEventDeleted}
          onOpenInGoogleCalendar={onOpenInGoogleCalendar}
          onOpenVideoConferencing={onOpenVideoConferencing}
          onCancelPressed={
            config?.inProductCalendar?.showCalendarPopupCancel && !isDAEngageProviderAndNotMigrated
              ? onCancelPressed
              : undefined
          }
          onCancelMosAppointmentPressed={
            calendarProvider?.booking_mode === BookingMode.HYBRID && mosApproved
              ? onCancelMosAppointmentPressed
              : undefined
          }
          onOpenPopover={
            config?.inProductCalendar?.triggerOnOpenPopover && !isDAEngageProviderAndNotMigrated
              ? onOpenPopover
              : undefined
          }
          onReschedule={
            config?.inProductCalendar?.showCalendarPopupReschedule && !isDAEngageProviderAndNotMigrated
              ? onReschedule
              : undefined
          }
          onCloseSession={
            config?.inProductCalendar?.showCalendarPopupClose && !isDAEngageProviderAndNotMigrated
              ? onCloseSession
              : undefined
          }
          onCompleteNote={
            config?.inProductCalendar?.showCalendarPopupClose && !isDAEngageProviderAndNotMigrated
              ? onCompleteNote
              : undefined
          }
          onUpdateModality={
            shouldShowModalitySection && !isDAEngageProviderAndNotMigrated ? onUpdateModality : undefined
          }
          getUpcomingAppointmentIds={
            shouldShowModalitySection && !isDAEngageProviderAndNotMigrated ? getUpcomingAppointmentIds : undefined
          }
          onViewCompletedNote={
            config?.inProductCalendar?.showCalendarPopupClose && !isDAEngageProviderAndNotMigrated
              ? onViewCompletedNote
              : undefined
          }
          onOpenZendeskPressed={() => {
            //TODO: May need unique mixpanel event for DA: DACN-1336
            track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.NAVIGATION_CALENDAR_ZENDESK })
            window.open(
              config?.inProductCalendar?.useInternationalLinks
                ? CALENDAR_HEADER_HELP_LINK_OUS
                : config?.inProductCalendar?.hybridBookingMode
                  ? CALENDAR_HEADER_HELP_LINK_DA
                  : (config?.inProductCalendar?.manageCalendarZendeskArticle ??
                    'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/35842493481363--PRODUCT-UPDATE-Calendar-Schedule-Feature-Day-to-Day-Calendar-Management'),
              '_blank',
            )
          }}
          getAppointmentBookedDetails={getAppointmentBookedDetails}
          getAlerts={getAlerts}
          hidePerformanceMetrics={config?.hidePerformanceMetrics}
          hybridBookingMode={config?.inProductCalendar?.hybridBookingMode}
          showSessionSubmission={config?.inProductCalendar?.showSessionSubmission}
          getSessionsAwaitingSubmissionCount={getSessionsAwaitingSubmissionCount}
          hideCalendarEventDetails={config?.inProductCalendar?.hideCalendarEventDetails}
          displayAppointmentBookedDetails={config?.inProductCalendar?.displayAppointmentBookedDetails}
          showRiskAlerts={!config?.hideRiskAlerts}
          calendarProvider={calendarProvider}
          showModalitySelection={shouldShowModalitySection}
          inPersonPreference={inPersonPreference}
          videoPreference={videoPreference}
          renderVideoAsVirtual={mosApproved}
          providerAddresses={providerAddresses}
          engageDAProvider={isMigratedEngageDAProvider}
          supportsOnOffPlatformVideo={!!config?.supportsOnOffPlatformVideo}
          providerNotes={allNotes}
          notesFetched={notesFetched}
          clients={showInactiveClients ? allClients : clients}
          suggestedClients={suggestedClients}
          getBookingDetailsForClient={isLegacyProvider ? undefined : getBookingDetailsForClient}
          onScheduleSession={onScheduleSession}
          trackScheduleNewSessionEntryPoint={trackScheduleNewSessionEntryPoint}
          rightHeaderSection={
            config?.showCalendarCapacity && isCapacityRolledOut && user ? (
              <CapacitiesCalendar provider={user} handleUpdateClick={openCapacityModalFromHook} />
            ) : undefined
          }
          enableBackdatedAppointments={config?.inProductCalendar?.enableBackdatedAppointments}
          enableSessionModalityEditability={config?.inProductCalendar?.enableSessionModalityEditability}
          isSelfPayEnabled={isSelfPayEnabled}
          showLateCancellations={config?.inProductCalendar?.showLateCancellations}
          splitVideoOptions={isDAEngageOnOffPlatformVideoEnabled && config?.supportsOnOffPlatformVideo}
          providerVideoPlatform={providerVideoPlatform}
          flexibleAvailabilityWindows={config?.inProductCalendar?.flexibleAvailabilityWindows}
          clientVoicemailConsent={clientVoicemailConsent}
          guardianVoicemailConsent={guardianVoicemailConsent}
          guardianPhone={guardianPhone}
          clientPronouns={clientPronouns}
          isClientDataLoading={isClientDataLoading}
          isNPSession={config?.inProductCalendar?.showNpSessionSelection}
          hiddenDays={config?.inProductCalendar?.hiddenDays}
          slotMinTime={isSupervisor && isTimezoneSelectorEnabled ? undefined : config?.inProductCalendar?.dailyStart}
          slotMaxTime={isSupervisor && isTimezoneSelectorEnabled ? undefined : config?.inProductCalendar?.dailyEnd}
          isLegacyProvider={isLegacyProvider}
          isLegacyClientListLoading={isLegacyClientListLoading}
          conflictStrategy={conflictStrategy}
          conflictTooltipText={conflictTooltipText}
          backgroundAvailability={shouldOverlapAvailability}
        />
      </CalendarContainer>
      <CalendarIsLiveConfirmationModal
        visible={showCalendarLiveModal}
        onClosePress={() => dispatch(hideCalendarLiveModal())}
      />
      <DAInvoiceFailedModal
        visible={showInvoiceFailedModal}
        onRequestClose={() => setShowInvoiceFailedModal(false)}
        clientName={daInvoiceState?.clientName || ''}
        sessionNumber={daInvoiceState?.sessionNumber}
      />
      <ConfirmationModal
        allowEmptyContents
        visible={mosAppointmentToCancel != null}
        onRequestClose={() => {
          if (!isCancelingMosAppointment) {
            setMosAppointmentToCancel(null)
          }
        }}
        onConfirmationButtonPress={confirmCancelMosAppointment}
        modalTitle={intl.formatMessage({
          defaultMessage: 'Cancel appointment?',
          description: 'Title of confirmation modal for canceling a MOS appointment',
        })}
        modalContents={null}
        confirmationButtonText={intl.formatMessage({
          defaultMessage: 'Confirm',
          description: 'Button confirming cancellation of a MOS appointment',
        })}
        cancelButtonText={intl.formatMessage({
          defaultMessage: 'Go back',
          description: 'Button returning without canceling a MOS appointment',
        })}
        confirmButtonLoading={isCancelingMosAppointment}
        confirmButtonDisabled={isCancelingMosAppointment}
        cancelButtonDisabled={isCancelingMosAppointment}
        showCloseIcon={!isCancelingMosAppointment}
        containerStyle={{ flexGrow: 0, minHeight: 0 }}
        testID={tID('CancelMosAppointmentModal')}
      />
      {capacityFlowModals}
    </>
  )
}

type CurrentCalendarProps = {
  actions: {
    getProvider: (params: Parameters<typeof getProvider>[0]) => Promise<any>
    setToastContent: (params: Parameters<typeof setToastContent>[0]) => Promise<any>
    updateProviderCapacity: (params: Parameters<typeof updateProviderCapacity>[0]) => Promise<any>
    updateSelectedProviderCapacity: (params: Parameters<typeof updateSelectedProviderCapacity>[0]) => Promise<any>
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      getProvider: getProvider as any,
      setToastContent: setToastContent as any,
      updateProviderCapacity: updateProviderCapacity as any,
      updateSelectedProviderCapacity: updateSelectedProviderCapacity as any,
    },
    dispatch,
  ),
})

const connector = connect(null, mapDispatchToProps)

export default connector(CurrentCalendar)
